/* eslint-disable @typescript-eslint/no-explicit-any */
import { useContext, useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  AlertBannerContext,
  API_RESPONSE_CODE
} from "@kivunova/kivufrontendcommon";
import { fetchGraphQL } from "../../Graphql/utils";
import { getUserQuery } from "../../Graphql/querries/userQuery";

// Shared across all components for the whole browser session.
const userNameCache = new Map<string, string>();
const inflight = new Map<string, Promise<string>>();

export function useUserDisplayName() {
  const { setAlert } = useContext(AlertBannerContext);

  const { mutateAsync } = useMutation<string, Error, string>({
    mutationKey: ["getUserDisplayName"],

    mutationFn: async (id: string) => {
      const response = await fetchGraphQL(
        getUserQuery,
        { input: { userId: id } },
        setAlert
      );

      const { code, user } = (response as any)?.getUser ?? {};

      if (code === API_RESPONSE_CODE.OK && user) {
        const name =
          [user.firstName, user.lastName].filter(Boolean).join(" ").trim() ||
          id;
        userNameCache.set(id, name);
        return name;
      }

      // API didn't return a usable user, fall back to the raw id, don't cache
      return id;
    }
  });

  const resolve = useCallback(
    async (userId: string | undefined | null): Promise<string> => {
      if (!userId || !userId.trim()) return "-";
      const id = userId.trim();

      // Already resolved before, no need to hit the network again
      if (userNameCache.has(id)) return userNameCache.get(id)!;

      // Someone else is already fetching this id, piggyback on their request
      if (inflight.has(id)) return inflight.get(id)!;

      // New fetch, track it so concurrent callers can share it
      const promise = mutateAsync(id)
        .catch(() => id)
        .finally(() => inflight.delete(id));

      inflight.set(id, promise);
      return promise;
    },
    [mutateAsync]
  );

  return { resolve };
}
